import { useState } from "react";
import styled, { css } from "styled-components";
import { faMicrochip, faMemory, faServer } from "@fortawesome/pro-duotone-svg-icons";
import Grid from "assets/homepage/pricing/Grid.svg";
import { SectionSubtitle, TopSectionContainer } from "screens/Homepage/styles";
import { typography } from "screens/Homepage/components/typography";
import { BoxedIcon } from "@beamcloud/design-system";
import RateUnitSelect, { TUnit } from "../RateUnitSelect";
import { hexToRgba } from "utils/ColorUtil";
import InfoBox from "./info-box";

const CPU_RATE_PER_SECOND = 0.00002778;
const RAM_RATE_PER_SECOND = 0.00000069;
const T4_GPU_RATE_PER_SECOND = 0.00017222222;
const A10G_GPU_RATE_PER_SECOND = 0.00046716;
const A100_GPU_RATE_PER_SECOND = 0.00177361;
const L4_GPU_RATE_PER_SECOND = 0.00027222222;

const ONE_HOUR_IN_SECONDS = 3600;
const ONE_MINUTE_IN_SECONDS = 60;

const HeroSection: React.FC = () => {
  const [unit, setUnit] = useState<TUnit>("s");

  // Hourly GPU rates are rounded to the tens place
  const renderRateValue = (valuePerSecond: number) => {
    switch (unit) {
      case "h":
        return (valuePerSecond * ONE_HOUR_IN_SECONDS).toFixed(2);
      case "m":
        return (valuePerSecond * ONE_MINUTE_IN_SECONDS).toFixed(2);
      default:
        return valuePerSecond.toFixed(6);
    }
  };

  // Hourly compute rates are rounded to the thousandth place
  const renderComputeRateValue = (valuePerSecond: number) => {
    switch (unit) {
      case "h":
        return (valuePerSecond * ONE_HOUR_IN_SECONDS).toFixed(3);
      case "m":
        return (valuePerSecond * ONE_MINUTE_IN_SECONDS).toFixed(3);
      default:
        return valuePerSecond.toFixed(7);
    }
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>Run Beam on your own infrastructure</Title>
        <Subtitle>
          Install Beam in any environment where you run GPUs, whether that's AWS or a workstation in your home
        </Subtitle>
        {/* <InfoBox /> */}
      </TitleWrapper>
    </Container>
  );
};

export default HeroSection;

const Container = styled(TopSectionContainer)`
  ${({ theme }) => {
    return css`
      background-image: radial-gradient(
          50% 50% at 50% 50%,
          ${hexToRgba(theme.colors.gray1, 0)} 0%,
          ${hexToRgba(theme.colors.gray1, 0.24)} 70%,
          ${hexToRgba(theme.colors.gray1, 1)} 100%
        ),
        url(${Grid});
      background-size: 100% 100%, cover;
      background-position: center, center;
      background-repeat: no-repeat;
    `;
  }}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0px;
  gap: 24px;
`;

const Title = styled.h1`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray12};
      max-width: 700px;

      ${typography.title36};

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.title44};
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        ${typography.title48};
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        ${typography.title56};
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        ${typography.title60};
      }
    `;
  }}
`;

const Subtitle = styled(SectionSubtitle)`
  text-align: center;
`;

const BillingRatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 20px;
  width: 100%;
`;

const BillingRatesCard = styled.div`
  ${({ theme }) => {
    return css`
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 24px 40px;
      box-sizing: border-box;
      gap: 32px;
      width: 100%;
      max-width: 640px;
      background-color: ${theme.colors.gray2};
      border: 1px solid ${theme.colors.gray6};
      box-shadow: ${theme.shadows.sm};
      border-radius: 8px;

      margin-bottom: 24px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        margin-bottom: 22px;
      }
    `;
  }}
`;

const StorageCard = styled(BillingRatesCard)`
  padding: 20px 24px;
`;

const RateDetails = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0px;
      gap: 16px;
      width: 100%;
    `;
  }}
`;

const StyledBoxedIcon = styled(BoxedIcon).attrs({
  dimension: 36,
  iconSize: "sm"
})`
  && {
    ${({ theme }) => {
      return css`
        display: inline-flex;
        background-color: ${theme.colors.gray4};
        border: 1px solid ${theme.colors.gray8};
        color: ${theme.colors.gray12};
      `;
    }}
  }
`;

const RateTitle = styled.span`
  ${({ theme }) => {
    return css`
      flex-grow: 1;
      color: ${theme.colors.gray12};
      ${typography.title20};

      @media (max-width: ${theme.breakpoints.sm}px) {
        ${typography.title18};
      }
    `;
  }}
`;

const RateDescription = styled.span`
  ${({ theme }) => {
    return css`
      color: ${theme.colors.gray11};
      ${typography.textLgLight};

      @media (max-width: ${theme.breakpoints.sm}px) {
        ${typography.textBaseLight};
      }
    `;
  }}
`;

const CircleBoxedIcon = styled(StyledBoxedIcon)`
  ${({ theme }) => {
    return css`
      && {
        border-radius: 50%;
        box-shadow: ${theme.shadows.sm};
      }
    `;
  }}
`;
